/**
 * Loads the Pit Link wordmark once (or any custom logo URL), caches the
 * data-URL PER-URL, and hands it back to any caller wanting to embed the
 * real brand asset in a jsPDF header. Async because we fetch the PNG and
 * convert via a `FileReader` — this keeps the JS bundle skinny.
 *
 * `LOGO_VERSION` gets bumped every time we swap the underlying asset so the
 * browser (and the module-level cache) invalidate cleanly.
 *
 * Feb 28 update — supports per-site custom PDF header logos (see
 * `siteLogoUrlFor()` below). If a site has its own logo, we load THAT and
 * use it for the header; the Pit Link wordmark still watermarks every
 * page as the platform-of-record.
 */

const LOGO_VERSION = "pl-2026-09-03-eroded";
const cache = new Map();      // key: URL → { dataUrl, w, h }
const inflight = new Map();   // key: URL → Promise

/**
 * loadLogo(url?) — fetches + caches a single logo asset.
 * @param {string} url  Full URL, `/relative/path.png`, or a `data:image/…` URL.
 */
export function loadLogo(url = "/shiftlog-logo-light.png") {
  const key = url;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);

  const promise = (async () => {
    try {
      let dataUrl;
      if (typeof url === "string" && url.startsWith("data:image/")) {
        // Already a data URL — no fetch needed.
        dataUrl = url;
      } else {
        const versioned = url.includes("?") ? `${url}&v=${LOGO_VERSION}` : `${url}?v=${LOGO_VERSION}`;
        const res = await fetch(versioned, { cache: "no-cache" });
        if (!res.ok) throw new Error(`logo fetch ${res.status}`);
        const blob = await res.blob();
        dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(blob);
        });
      }
      // Grab intrinsic dimensions so callers can preserve the aspect ratio.
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
        img.onerror = () => resolve({ w: 0, h: 0 });
        img.src = dataUrl;
      });
      const entry = { dataUrl, ...dims };
      cache.set(key, entry);
      return entry;
    } catch {
      const empty = { dataUrl: null, w: 0, h: 0 };
      cache.set(key, empty);
      return empty;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}

/**
 * loadPdfBrand(item) — resolve BOTH the header logo (site-specific if the
 * item's `site_config.pdf_header_logo` is set, else the standard SE mark)
 * and the watermark logo (always the SE mark). Returns:
 *   { header: {dataUrl,w,h}, watermark: {dataUrl,w,h} }
 *
 * If `item` doesn't carry `site_config`, we also try `item.pdf_header_logo`
 * directly so callers who already resolved the site config can pass it in
 * flat. Falls back gracefully to the standard SE wordmark for both.
 */
/**
 * looksOpaqueDarkBackground(res) — after a candidate custom logo has been
 * loaded, check whether it is a solid-dark-background image (the exact
 * "black box on white PDF" defect Josh reported). We use a tiny in-memory
 * canvas to sample the 4 corners: if none have alpha < 20 AND all four are
 * near-black, we reject the asset and fall back to the default mark.
 */
async function looksOpaqueDarkBackground(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.onload = () => {
        try {
          const c = document.createElement("canvas");
          c.width = img.naturalWidth; c.height = img.naturalHeight;
          const ctx = c.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const w = c.width, h = c.height;
          const pts = [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]];
          let opaqueDarkCorners = 0;
          for (const [x, y] of pts) {
            const [r, g, b, a] = ctx.getImageData(x, y, 1, 1).data;
            if (a > 200 && Math.max(r, g, b) < 60) opaqueDarkCorners += 1;
          }
          // 3 of 4 dark opaque corners = definitely a black-background asset.
          resolve(opaqueDarkCorners >= 3);
        } catch { resolve(false); }
      };
      img.onerror = () => resolve(false);
      img.src = dataUrl;
    } catch { resolve(false); }
  });
}

export async function loadPdfBrand(item = {}) {
  const custom =
    item?.pdf_header_logo ||
    item?.site_pdf_header_logo ||
    item?.custom_logo_data_url ||
    item?.site_config?.pdf_header_logo ||
    item?.site_config?.custom_logo_data_url ||
    item?.site_config?.site_logo_url;
  const watermark = await loadLogo("/shiftlog-logo-light.png");
  let header = watermark;
  if (custom) {
    const candidate = await loadLogo(custom);
    if (candidate && candidate.dataUrl) {
      const bad = await looksOpaqueDarkBackground(candidate.dataUrl);
      if (bad) {
        // eslint-disable-next-line no-console
        console.warn("[pdfLogo] Rejected opaque dark-background custom logo — using default Pit Link mark instead.");
      } else {
        header = candidate;
      }
    }
  }
  return { header, watermark };
}
